/**
 * Terms of Service Page - Olympic Fit Wellness
 */
import { Link } from 'react-router-dom';

export default function TermsPage() {
  return (
    <div className="bg-white" data-testid="terms-page">
      {/* Hero */}
      <section className="relative pt-32 pb-16 bg-neutral-900">
        <div className="container mx-auto px-6 lg:px-12 text-center">
          <span className="text-sm font-heading tracking-[0.15em] text-neutral-500 mb-4 block">LEGAL</span>
          <h1 className="text-4xl md:text-5xl font-heading font-normal text-white mb-4 tracking-[0.04em] uppercase">
            Terms of Service
          </h1>
          <p className="text-neutral-400">Last updated: February 2026</p>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 lg:py-24">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="max-w-3xl mx-auto prose prose-neutral">
            <div className="space-y-8">
              <div>
                <h2 className="text-2xl font-heading font-normal text-neutral-900 mb-4 tracking-wide">1. Acceptance of Terms</h2>
                <p className="text-neutral-600 leading-relaxed">
                  By accessing or using Olympic Fit Wellness facilities, website, mobile application, or any related services, you agree to be bound by these Terms of Service. If you do not agree to these terms, please do not use our services.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-heading font-normal text-neutral-900 mb-4 tracking-wide">2. Membership Terms</h2>
                <p className="text-neutral-600 leading-relaxed mb-4">
                  All memberships are subject to the following terms:
                </p>
                <ul className="list-disc list-inside text-neutral-600 space-y-2">
                  <li>Memberships are non-transferable</li>
                  <li>Monthly dues are automatically charged on your billing date</li>
                  <li>Cancellations require 30 days written notice</li>
                  <li>Membership freezes are limited to 3 months per year</li>
                  <li>Access is granted to all Olympic Fit Wellness locations based on your membership tier</li>
                </ul>
              </div>

              <div>
                <h2 className="text-2xl font-heading font-normal text-neutral-900 mb-4 tracking-wide">3. Facility Rules</h2>
                <p className="text-neutral-600 leading-relaxed mb-4">
                  Members agree to follow all facility rules, including:
                </p>
                <ul className="list-disc list-inside text-neutral-600 space-y-2">
                  <li>Proper attire and footwear required at all times</li>
                  <li>Equipment must be wiped down after use</li>
                  <li>Weights must be re-racked after use</li>
                  <li>No photography or video without express permission</li>
                  <li>Children under 14 must be supervised by an adult at all times</li>
                  <li>Respectful behavior toward staff and other members</li>
                </ul>
              </div>

              <div>
                <h2 className="text-2xl font-heading font-normal text-neutral-900 mb-4 tracking-wide">4. Class Booking Policy</h2>
                <p className="text-neutral-600 leading-relaxed">
                  Classes may be booked up to 7 days in advance. Cancellations made less than 4 hours before class time may result in a booking restriction. Repeated no-shows may result in loss of advance booking privileges.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-heading font-normal text-neutral-900 mb-4 tracking-wide">5. Health Disclaimer</h2>
                <p className="text-neutral-600 leading-relaxed">
                  You understand that physical exercise involves inherent risks of injury. By using our facilities and services, you assume all risks associated with physical activity. We recommend consulting with a physician before beginning any exercise program.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-heading font-normal text-neutral-900 mb-4 tracking-wide">6. Liability Waiver</h2>
                <p className="text-neutral-600 leading-relaxed">
                  To the fullest extent permitted by law, Olympic Fit Wellness, its officers, employees, and agents shall not be liable for any injury, loss, or damage arising from your use of our facilities or services, except in cases of gross negligence or willful misconduct.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-heading font-normal text-neutral-900 mb-4 tracking-wide">7. Personal Training Services</h2>
                <p className="text-neutral-600 leading-relaxed">
                  Personal training packages are non-refundable once sessions have commenced. Sessions expire 6 months from purchase date. Cancellations require 24 hours notice to avoid forfeiture of the session.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-heading font-normal text-neutral-900 mb-4 tracking-wide">8. Intellectual Property</h2>
                <p className="text-neutral-600 leading-relaxed">
                  All content on our website and mobile application, including text, graphics, logos, and software, is the property of Olympic Fit Wellness and is protected by copyright and trademark laws. Unauthorized use is prohibited.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-heading font-normal text-neutral-900 mb-4 tracking-wide">9. Account Security</h2>
                <p className="text-neutral-600 leading-relaxed">
                  You are responsible for maintaining the confidentiality of your account credentials. You agree to notify us immediately of any unauthorized use of your account.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-heading font-normal text-neutral-900 mb-4 tracking-wide">10. Termination</h2>
                <p className="text-neutral-600 leading-relaxed">
                  We reserve the right to terminate or suspend your membership at any time for violation of these terms, facility rules, or any conduct we deem harmful to our business, staff, or other members.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-heading font-normal text-neutral-900 mb-4 tracking-wide">11. Governing Law</h2>
                <p className="text-neutral-600 leading-relaxed">
                  These Terms shall be governed by and construed in accordance with the laws of the State of Texas, without regard to conflict of law principles.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-heading font-normal text-neutral-900 mb-4 tracking-wide">12. Changes to Terms</h2>
                <p className="text-neutral-600 leading-relaxed">
                  We reserve the right to modify these Terms at any time. Changes will be posted on our website and become effective immediately upon posting. Continued use of our services constitutes acceptance of modified terms.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-heading font-normal text-neutral-900 mb-4 tracking-wide">13. Contact Information</h2>
                <p className="text-neutral-600 leading-relaxed">
                  For questions regarding these Terms of Service, please contact:
                </p>
                <div className="mt-4 p-6 bg-neutral-50">
                  <p className="text-neutral-700"><strong>Olympic Fit Wellness</strong></p>
                  <p className="text-neutral-600">Legal Department</p>
                  <p className="text-neutral-600">1100 N Central Expy, McKinney, TX 75070</p>
                  <p className="text-neutral-600">Email: legal@olympicfitwellness.com</p>
                  <p className="text-neutral-600">Phone: (469) 555-0100</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Back to Home */}
      <section className="py-12 border-t border-neutral-200">
        <div className="container mx-auto px-6 lg:px-12 text-center">
          <Link to="/" className="text-neutral-600 hover:text-neutral-900 transition-colors">
            ← Back to Home
          </Link>
        </div>
      </section>
    </div>
  );
}
